/**
 * Метаданные страницы.
 *
 * clapme — SPA: заголовок и описание в `index.html` одни на все маршруты.
 * Для человека это мелочь, но вкладка с пультом и вкладка с экраном эфира
 * подписаны одинаково, а поисковик, который умеет исполнять JS, видит на
 * `/join/ABCD` заголовок главной.
 *
 * Отсюда две задачи: подписать вкладки по-человечески и закрыть от индексации
 * всё, кроме главной. `robots.txt` запрещает обход, но не индексацию: адрес,
 * на который кто-то сослался, может попасть в выдачу и без обхода — вот его
 * из выдачи и убирает `noindex` в разметке.
 */
import { useEffect } from 'react'

export const SITE_URL = 'https://clapme.ru'

/** Заголовок главной: должен совпадать с `<title>` в index.html. */
export const HOME_TITLE = 'clapme — услышьте весь зал, а не первый ряд'

export interface Seo {
  /** Полный `<title>` страницы. */
  title: string
  /** `meta[name=description]`. Не задан — остаётся описание из index.html. */
  description?: string
  /** Канонический адрес; относительный путь достраивается до полного. */
  canonical?: string
  /** Закрыть страницу от индексации. */
  noindex?: boolean
}

function upsertMeta(name: string, content: string) {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`)
  if (!tag) {
    tag = document.createElement('meta')
    tag.name = name
    document.head.appendChild(tag)
  }
  tag.content = content
}

export function applySeo({ title, description, canonical, noindex }: Seo) {
  document.title = title

  if (description) upsertMeta('description', description)

  if (canonical) {
    const href = canonical.startsWith('http') ? canonical : SITE_URL + canonical
    let link = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
    if (!link) {
      link = document.createElement('link')
      link.rel = 'canonical'
      document.head.appendChild(link)
    }
    link.href = href
  }

  // Снимать тег так же важно, как ставить: без этого возврат на главную из
  // закрытого эфира оставил бы `noindex` на индексируемой странице.
  const robots = document.head.querySelector<HTMLMetaElement>('meta[name="robots"]')
  if (noindex) upsertMeta('robots', 'noindex, nofollow')
  else if (robots) robots.content = 'index, follow, max-image-preview:large'
}

/** То же самое, но привязано к жизни экрана. */
export function useSeo(seo: Seo) {
  const { title, description, canonical, noindex } = seo
  useEffect(() => {
    applySeo({ title, description, canonical, noindex })
  }, [title, description, canonical, noindex])
}
